/**
 * Turning a filter verdict into a *label*.
 *
 * The evaluator answers what the player should be told about an item; this answers how
 * loud the label is. It is deliberately only half of the presentation mapping: the
 * beam's half lives in `game/loot.ts` beside the mesh it configures, because the floor
 * on a beam's width comes from `game/pixelate.ts` and that module imports Babylon.
 * Pulling it in here would put Babylon in the import graph of a directory whose whole
 * claim is that it has none.
 *
 * Pure: no DOM, no Babylon, no clock. Colours are `#rrggbb` or **null**, and null means
 * "use the rarity colour you already have" rather than a hardcoded default — the CSS
 * side has `--rarity-*` tokens and the scene has `RARITY_COLOUR`, and inventing a third
 * palette here would be a third source of truth.
 *
 * ## The one rule both halves share
 *
 * **Only a rule hides anything.** `evaluateItem`'s built-in fallback shows Rare and
 * better, which is the right policy for a *label* — a new overlay, so there is nothing
 * to take away — but applying it to the beam and the drop chime would silently strip
 * Normal and Magic drops from a player who has never written a filter. The spec's own
 * wording agrees: its Filtered Out row reads "junk gear matching player filter **hide
 * rules**". `ruleHides` is that distinction, and both halves ask it.
 */

import type { FilterTier, FilterVerdict } from './evaluate';

/** How a floating item label should be drawn. */
export interface LabelStyle {
  tier: FilterTier;
  /** `SetTextColor`, or null to fall back on the rarity token. */
  textColour: string | null;
  /** `SetBorderColor`, or null to fall back on the rarity token. */
  borderColour: string | null;
  /** CSS pixels. Never zero, so a label is never invisible-but-present. */
  fontSize: number;
}

/**
 * Font size per tier, in CSS pixels, when the filter did not say.
 *
 * **Chosen.** The spec ranks the tiers but gives no sizes. These are anchored on the
 * existing `.loot-prompt__name` at 10.5px — that is what a drop's name looks like in
 * this HUD today, so Tier 4 is exactly it and the louder tiers step up from there. The
 * ladder is strictly monotonic on purpose: a Tier 2 label that read smaller than a
 * Tier 3 one would invert the whole point of the ranking.
 */
export const TIER_FONT_SIZE: Readonly<Record<FilterTier, number>> = Object.freeze({
  1: 17,
  2: 14,
  3: 12,
  4: 10.5,
});

/** Whether a rule — not the built-in fallback — decided to hide this item. */
export function ruleHides(verdict: FilterVerdict): boolean {
  return verdict.source === 'RULE' && !verdict.visible;
}

/**
 * How to draw the label for an item the caller has decided to label.
 *
 * **Accepts no verdict**, matching `beamStyle`, which has taken `FilterVerdict | undefined` since
 * it was written. That case is no longer only defensive: since the Alt reveal and the drop
 * impulse, a label can be drawn for a drop the filter declined to describe, and the tier-4
 * presentation — the plainest one, and what a drop's name has always looked like — is the honest
 * thing to draw for it.
 */
export function labelStyle(verdict: FilterVerdict | undefined): LabelStyle {
  const tier: FilterTier = verdict?.tier ?? 4;
  const size = verdict?.style.fontSize;
  return {
    tier,
    textColour: verdict?.style.textColour ?? null,
    borderColour: verdict?.style.borderColour ?? null,
    // A zero or negative size cannot come out of the parser, which bounds
    // `SetFontSize` to 8-48 — but the type says `number`, and a label with no height
    // is one the player cannot see and cannot be told about.
    fontSize: size !== undefined && size > 0 ? size : TIER_FONT_SIZE[tier],
  };
}

// ── Label geometry ──────────────────────────────────────────────────────────

/**
 * Padding around a label's text, matching `.loot-label`'s CSS.
 *
 * Duplicated from the stylesheet rather than measured, because the layout solver needs
 * boxes *before* the DOM exists — measuring would mean rendering every label at its
 * anchor, reading it back, and moving it, which is a frame of visible jitter per drop.
 * `ui/tooltip.ts` measures because a tooltip is one element on demand; a dozen labels
 * every frame is a different problem.
 */
const LABEL_PADDING_X = 9;
const LABEL_PADDING_Y = 3;

/**
 * Average glyph advance as a fraction of the font size, for the display serif.
 *
 * **An estimate, and deliberately a generous one.** Over-estimating a width spaces the
 * stack out slightly; under-estimating lets two labels the solver believes are clear
 * actually overlap, which is the one failure the solver exists to prevent. 0.58 is wide
 * for a serif at these sizes.
 */
const GLYPH_ASPECT = 0.58;

/** Line height as a multiple of the font size. */
const LINE_HEIGHT = 1.35;

/** A label's box in CSS pixels, without laying it out. */
export function estimateLabelBox(
  name: string,
  fontSize: number,
): { width: number; height: number } {
  const glyphs = Math.max(1, name.length);
  return {
    width: Math.ceil(glyphs * fontSize * GLYPH_ASPECT + LABEL_PADDING_X * 2),
    height: Math.ceil(fontSize * LINE_HEIGHT + LABEL_PADDING_Y * 2),
  };
}
